import React, { Component } from "react";
import { Button } from 'reactstrap';
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setSuccess, setLoader } from "../../../actions/action-creators";
import moment from "moment";
import { Switch, TablePagination, Icon } from "@material-ui/core";
import InputFeild from "../../global/form/input";
import ConfirmAction from "../../global/confirm-action";
import { downloadApiFile } from "../../../actions";

const RETRYABLE_STATUS = [1, 4]; // Bank Details Missing, Failed
const INITIATABLE_STATUS = [0]; // created (e.g. monthly payout) but not sent to bank yet

const fmtDate  = (v) => { const m = moment(v); return (v && m.isValid()) ? m.utcOffset("+05:30").format("Do MMM'YY") : "—"; };
const fmtINR   = (v) => v != null ? "₹" + Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";
const orDash   = (v) => (v != null && v !== "") ? v : "—";

const PAYOUT_CYCLES = [
    { value: "OFF",     label: "Off (Hold)", bg: "#fff0f0", color: "#c0392b" },
    { value: "WEEKLY",  label: "Weekly",     bg: "#e6f7ee", color: "#1a7a42" },
    { value: "MONTHLY", label: "Monthly",    bg: "#e8f1ff", color: "#1677ff" },
];

const PAYOUT_STATUS = {
    0: { label: "Pending Initiation",   bg: "#e8f1ff", color: "#1677ff" },
    1: { label: "Bank Details Missing", bg: "#fff0f0", color: "#c0392b" },
    2: { label: "Processing",           bg: "#fff7e6", color: "#d46b08" },
    3: { label: "Processing",           bg: "#fff7e6", color: "#d46b08" },
    4: { label: "Failed",               bg: "#fff0f0", color: "#c0392b" },
    5: { label: "Paid",                 bg: "#e6f7ee", color: "#1a7a42" },
};
const statusBadge = (status) => {
    const cfg = PAYOUT_STATUS[status] || { label: "—", bg: "#f0f0f0", color: "#888" };
    return (
        <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
            {status === 5 && "✓ "}{cfg.label}
        </span>
    );
};

class UserPayouts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            payoutData: [],
            userFlags: null,
            flagRows: [],
            hasVerifiedGst: false,
            currentPage: 0,
            editFlag: null, // { flag_key, flag_value, remark }
            // validation email period, defaults to last month
            requestYear: moment().subtract(1, "month").year(),
            requestMonth: moment().subtract(1, "month").month() + 1,
            isConfirmInvoiceRequest: false,
            isInvoiceRequestOpen: false, // validation email box, used for few (invoicing) users

        };
    }

    componentDidMount() {
        this.showPayoutData();
        this.fetchUserFlags();
    }

    downloadInvoiceRequestCsv = async () => {
        const { requestYear, requestMonth } = this.state;
        const month = `${requestYear}-${String(requestMonth).padStart(2, "0")}`;
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.UserInvoice.downloadInvoiceRequestCsv({ user_id: this.props.user_id, month });
            downloadApiFile(resp, `Statement - ${moment(`${month}-01`).format("MMMM YYYY")} - ${this.props.user_id}.csv`);
        } catch (e) {
            let message = e.toString();
            if (e.response && e.response.data) {
                // blob responseType: error body arrives as a Blob containing JSON
                try {
                    const body = e.response.data instanceof Blob ? JSON.parse(await e.response.data.text()) : e.response.data;
                    message = body.message || message;
                } catch (parseError) { /* keep generic message */ }
            }
            store.dispatch(setError({ show: true, message }));
        }
        store.dispatch(setLoader(false));
    };

    sendInvoiceRequest = async () => {
        const { requestYear, requestMonth } = this.state;
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.UserInvoice.sendInvoiceRequest({ user_id: this.props.user_id, month: `${requestYear}-${String(requestMonth).padStart(2, "0")}` });
            store.dispatch(setSuccess({ show: true, message: resp.data.message }));
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    fetchUserFlags = async () => {
        store.dispatch(setLoader(true));
        try {
            let resp = await ApiEndpoints.User.fetchUserFlags({ user_id: this.props.user_id });
            const data = resp.data.data || {};
            this.setState({ userFlags: data.flags || {}, flagRows: data.rows || [], hasVerifiedGst: !!data.has_verified_gst });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    showPayoutData = async () => {
        store.dispatch(setLoader(true));
        try {
            let resp = await ApiEndpoints.JobTaskPayment.fetchUserPayouts({ user_id: this.props.user_id, include_requested: 1 });
            let payoutData = resp.data.data.sort((a, b) => new Date(b.created_on) - new Date(a.created_on));
            this.setState({ payoutData });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    initiatePayoutData = async (payout_data_id) => {
        if (!window.confirm("Initiate bank transfer for this payout?")) return;
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.JobTaskPayment.initiatePayoutData({ payout_data_id });
            store.dispatch(setSuccess({ show: true, message: resp.data.message || "Payout initiated" }));
            await this.showPayoutData();
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    retryPayoutData = async (payout_data_id) => {
        if (!window.confirm("Are you sure?")) return;
        store.dispatch(setLoader(true));
        let { payoutData } = this.state;
        try {
            let resp = await ApiEndpoints.JobTaskPayment.retryPayoutData({ payout_data_id });
            payoutData = payoutData.map(data => {
                if (data.payout_data_id === payout_data_id) data = { ...data, ...(resp.data.data || {}) };
                return data;
            });
            this.setState({ payoutData });
            store.dispatch(setSuccess({ show: true, message: "Retry Payout request submitted successfully" }));
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    saveFlag = async () => {
        const { editFlag } = this.state;
        if (!editFlag) return;
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.User.updateUserFlag({ user_id: this.props.user_id, ...editFlag });
            store.dispatch(setSuccess({ show: true, message: resp.data.message || "Updated" }));
            this.setState({ editFlag: null });
            await this.fetchUserFlags();
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    flagRemark = (flag_key) => {
        const row = this.state.flagRows.find(r => r.flag_key === flag_key);
        return row ? row.remark : null;
    };

    render() {
        let { payoutData, userFlags, hasVerifiedGst, editFlag, currentPage, requestYear, requestMonth, isConfirmInvoiceRequest, isInvoiceRequestOpen } = this.state;
        const currentYear = moment().year();
        const requestYears = [currentYear, currentYear - 1, currentYear - 2];
        // months of the current year after this month can't be picked
        const maxMonth = Number(requestYear) === currentYear ? moment().month() + 1 : 12;
        const requestLabel = moment(`${requestYear}-${String(requestMonth).padStart(2, "0")}-01`).format("MMMM YYYY");
        const page   = payoutData.slice(currentPage * 10, currentPage * 10 + 10);
        const cycle  = editFlag && editFlag.flag_key === "PAYOUT_CYCLE" ? editFlag.flag_value : userFlags && userFlags.PAYOUT_CYCLE;
        const invoicing = editFlag && editFlag.flag_key === "INVOICING" ? editFlag.flag_value === "1" : !!userFlags && userFlags.INVOICING === "1";
        const cycleCfg = PAYOUT_CYCLES.find(c => c.value === cycle) || PAYOUT_CYCLES[1];
        const cycleRemark = this.flagRemark("PAYOUT_CYCLE");

        return (
            <div>
                {/* ── Payout Settings Card ───────────────────────────── */}
                {!!userFlags && (
                    <div style={S.statusCard}>
                        <div style={S.statusLeft}>
                            <span style={{ ...S.statusDot, background: cycleCfg.color }} />
                            <span style={S.statusLabel}>Payout Cycle</span>
                            <div style={S.segment}>
                                {PAYOUT_CYCLES.map(c => (
                                    <span
                                        key={c.value}
                                        style={{ ...S.segmentItem, ...(cycle === c.value ? { background: c.bg, color: c.color, fontWeight: 700 } : {}) }}
                                        onClick={() => {
                                            if (editFlag && editFlag.flag_key !== "PAYOUT_CYCLE") return;
                                            this.setState({ editFlag: c.value === userFlags.PAYOUT_CYCLE ? null : { flag_key: "PAYOUT_CYCLE", flag_value: c.value, remark: "" } });
                                        }}
                                    >
                                        {c.label}
                                    </span>
                                ))}
                            </div>
                            {cycleRemark && !editFlag && <span style={S.statusRemark}>{cycleRemark}</span>}
                        </div>
                        <div style={S.statusRight}>
                            {invoicing && !hasVerifiedGst && (
                                <span style={{ ...S.statusRemark, color: "#d46b08" }}>⚠ No verified GST document</span>
                            )}
                            <span style={S.statusLabel}>GST Invoicing</span>
                            <Switch
                                size="small"
                                color="primary"
                                checked={invoicing}
                                onClick={(e) => {
                                    if (editFlag && editFlag.flag_key !== "INVOICING") return;
                                    const value = e.target.checked ? "1" : "0";
                                    this.setState({ editFlag: value === userFlags.INVOICING ? null : { flag_key: "INVOICING", flag_value: value, remark: "" } });
                                }}
                            />
                           
                        </div>
                        {!!editFlag && (
                            <div style={S.editRow}>
                                <span style={{ fontSize: 12, color: "#555" }}>
                                    {editFlag.flag_key === "PAYOUT_CYCLE"
                                        ? `Change payout cycle to ${editFlag.flag_value}`
                                        : `Turn GST invoicing ${editFlag.flag_value === "1" ? "ON" : "OFF"}`}
                                </span>
                                <InputFeild
                                    style={{ fontSize: 12, margin: 0, width: 220 }}
                                    placeholder={editFlag.flag_value === "OFF" ? "Hold reason (optional)" : "Remark (optional)"}
                                    value={editFlag.remark || ""}
                                    onChange={(e) => this.setState({ editFlag: { ...editFlag, remark: e.target.value } })}
                                />
                                <Button size="sm" color="dark" style={{ fontSize: 12 }} onClick={this.saveFlag}>Save</Button>
                                <Button size="sm" color="link" style={{ fontSize: 12 }} onClick={() => this.setState({ editFlag: null })}>Cancel</Button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Validation Email (collapsed by default) ────────── */}
                {!isInvoiceRequestOpen ? (
                    <div style={{ textAlign: "right", marginTop: -12, marginBottom: 16 }}>
                        <span style={S.linkToggle} onClick={() => this.setState({ isInvoiceRequestOpen: true })}>Validation Email ▾</span>
                    </div>
                ) : (
                <div style={S.statusCard}>
                    <div style={S.statusLeft}>
                        <span style={S.statusLabel}>Validation Email</span>
                        <span style={S.linkToggle} onClick={() => this.setState({ isInvoiceRequestOpen: false })}>Hide ▴</span>
                        <span style={S.statusRemark}>Statement of the month's earnings + GST invoice request, with CSV (works before payout too)</span>
                    </div>
                    <div style={S.statusRight}>
                        <select
                            className="form-control form-control-sm"
                            style={{ width: 100 }}
                            value={requestYear}
                            onChange={(e) => {
                                const year = Number(e.target.value);
                                const lastMonth = year === currentYear ? moment().month() + 1 : 12;
                                this.setState({ requestYear: year, requestMonth: Math.min(requestMonth, lastMonth) });
                            }}
                        >
                            {requestYears.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                        <select
                            className="form-control form-control-sm"
                            style={{ width: 140 }}
                            value={requestMonth}
                            onChange={(e) => this.setState({ requestMonth: Number(e.target.value) })}
                        >
                            {moment.months().map((name, i) => (
                                <option key={name} value={i + 1} disabled={i + 1 > maxMonth}>{name}</option>
                            ))}
                        </select>
                        <Button size="sm" outline color="dark" style={{ fontSize: 12 }} onClick={this.downloadInvoiceRequestCsv}>
                            Download CSV
                        </Button>
                        <Button size="sm" color="dark" style={{ fontSize: 12 }} onClick={() => this.setState({ isConfirmInvoiceRequest: true })}>
                            Send Email
                        </Button>
                    </div>
                    {!!userFlags && userFlags.INVOICING !== "1" && (
                        <div style={{ ...S.statusRemark, color: "#d46b08", width: "100%" }}>
                            ⚠ GST invoicing is off for this user — the email asks them for a GST invoice.
                        </div>
                    )}
                </div>
                )}

                <ConfirmAction
                    isOpen={!!isConfirmInvoiceRequest}
                    message={`Send the validation email for ${requestLabel}?`}
                    onClose={() => this.setState({ isConfirmInvoiceRequest: false })}
                    callback={this.sendInvoiceRequest}
                />

                {/* ── Payout History ─────────────────────────────────── */}
                <div style={S.sectionTitle}>Payout History</div>

                {payoutData.length === 0 ? (
                    <div style={S.emptyState}>No payout data found.</div>
                ) : (
                    <>
                        <div style={S.tableWrapper}>
                            <table style={S.table}>
                                <thead>
                                    <tr style={S.tHead}>
                                        <th style={S.th}>Payout Title</th>
                                        <th style={S.th}>Amount</th>
                                        <th style={S.th}>Status</th>
                                        <th style={S.th}>Date</th>
                                        <th style={S.th}>Credited On</th>
                                        <th style={S.th}>Bank UTR</th>
                                        <th style={S.th}>Bank Req ID</th>
                                        <th style={S.th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {page.map((payout, i) => (
                                        <tr key={i} style={S.tRow}>
                                            <td style={S.td}>
                                                <span style={{ fontWeight: 700, color: "#1a1a1a" }}>
                                                    {payout.Payout ? payout.Payout.payout_title : "—"}
                                                </span>
                                            </td>
                                            <td style={{ ...S.td, fontWeight: 700 }}>
                                                {fmtINR(payout.amount)}
                                            </td>
                                            <td style={S.td}>
                                                {statusBadge(payout.status)}
                                            </td>
                                            <td style={{ ...S.td, whiteSpace: "nowrap", color: "#555" }}>
                                                {fmtDate(payout.created_on)}
                                            </td>
                                            <td style={{ ...S.td, whiteSpace: "nowrap", color: "#555" }}>
                                                {fmtDate(payout.amount_credited_on)}
                                            </td>
                                            <td style={{ ...S.td, fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                                                {orDash(payout.bank_utr)}
                                            </td>
                                            <td style={{ ...S.td, fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                                                {orDash(payout.bank_req_id)}
                                            </td>
                                            <td style={S.td}>
                                                {INITIATABLE_STATUS.includes(payout.status) &&
                                                    <Icon onClick={() => this.initiatePayoutData(payout.payout_data_id)} style={{ cursor: "pointer", fontSize: 18, color: "#1677ff" }} title="Initiate Payout">play_circle_filled</Icon>
                                                }
                                                {RETRYABLE_STATUS.includes(payout.status) &&
                                                    <Icon onClick={() => this.retryPayoutData(payout.payout_data_id)} style={{ cursor: "pointer", fontSize: 18, color: "#555" }} title="Retry Payout">refresh</Icon>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination
                            rowsPerPageOptions={[10]}
                            component="div"
                            count={payoutData.length}
                            rowsPerPage={10}
                            page={currentPage}
                            onPageChange={(_, p) => this.setState({ currentPage: p })}
                        />
                    </>
                )}
            </div>
        );
    }
}

const S = {
    statusCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        border: "1px solid #e8e8e8",
        borderRadius: 12,
        padding: "14px 20px",
        background: "#fff",
        marginBottom: 22,
    },
    statusLeft:  { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    statusRight: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    statusDot:   { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
    statusLabel: { fontSize: 15, fontWeight: 700, color: "#1a1a1a" },
    statusPill:  { borderRadius: 999, padding: "3px 14px", fontSize: 13, fontWeight: 600 },
    statusRemark:{ fontSize: 12, color: "#888" },
    linkToggle:  { fontSize: 12, color: "#1677ff", cursor: "pointer", userSelect: "none" },
    segment:     { display: "inline-flex", border: "1px solid #e0e0e0", borderRadius: 999, overflow: "hidden" },
    segmentItem: { padding: "3px 14px", fontSize: 13, color: "#555", cursor: "pointer", userSelect: "none" },
    editRow:     { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", width: "100%", borderTop: "1px dashed #eee", paddingTop: 10 },

    sectionTitle: { fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 12 },

    tableWrapper: { border: "1px solid #e8e8e8", borderRadius: 10, overflow: "auto" },
    table:   { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    tHead:   { background: "#f5f5f5" },
    th:      { padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "1px solid #e8e8e8", whiteSpace: "nowrap" },
    tRow:    { borderBottom: "1px solid #f0f0f0" },
    td:      { padding: "11px 14px", color: "#333", verticalAlign: "middle" },
    emptyState: { padding: "40px 20px", textAlign: "center", color: "#aaa", fontSize: 14 },
};

export default UserPayouts;
